import * as React from "react";
import { AccessRolesListDto, AccessRoleListItemDto } from "Api/Api";
import styled from "styled-components";
import {
  PaginationParams,
  PrimaryDataTable,
  TableIDType,
} from "Components/Shared/PrimaryDataTable/PrimaryDataTable";
import { Resources, useResource } from "Translations/Resources";
import { useHistory } from "react-router-dom";
import { Typography } from "@material-ui/core";
import { DetailColumn } from "Components/Shared/PrimaryDataTable/DetailColumn";
import { Dictionary } from "lodash";
import { FilterProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableFilter";
import { OrderByProps } from "Components/Shared/PrimaryDataTable/PrimaryDataTableOrderBy";
import { useHasCurrentUserAccessRight } from "Hooks/useHasCurrentUserAccessRight";
import { AccessRightCodes } from "Components/Models/AccessRights";

type Props = {
  accessRoles: AccessRolesListDto;
  onPageChange: (page: number, rows: number) => void;
  isLoading: boolean;
  resetTablePage?: boolean;
  filter: Dictionary<FilterProps>;
  orderBy: OrderByProps<AccessRoleListItemDto>;
  paginationParams?: PaginationParams | null;
};

const StyledTableWrapper = styled.div`
  margin-top: ${props => props.theme.spacing(3)}px;

  .table-wrapper .table thead > tr > th:nth-last-child(2) .filter-popup {
    left: 0;
    right: auto;
  }
`;

export const AccessRolesTable: React.FunctionComponent<Props> = props => {
  const {
    accessRoles,
    onPageChange,
    isLoading,
    resetTablePage,
    filter,
    orderBy,
    paginationParams,
  } = props;
  const history = useHistory();
  const { t } = useResource();

  const hasAccessRight = useHasCurrentUserAccessRight();
  const canEditAccessRoles = hasAccessRight([
    AccessRightCodes.Core_AccessRoles_Edit,
  ]);

  const onRowClick = (role: AccessRoleListItemDto) => {
    history.push(
      `/${t(Resources.Routes.AccessRoles)}/${t(
        Resources.Routes.AccessRoleDetail,
      )}/${role.accessRoleID}`,
    );
  };

  const getCell = (
    id: TableIDType<AccessRoleListItemDto>,
    role: AccessRoleListItemDto,
  ) => {
    switch (id) {
      case "accessRoleCode":
        return (
          <div>
            <Typography>{role.accessRoleCode}</Typography>
          </div>
        );

      case "name":
        return (
          <div>
            <Typography>{role.name}</Typography>
          </div>
        );

      case "detail":
        return canEditAccessRoles ? (
          <DetailColumn
            url={`/${t(Resources.Routes.AccessRoles)}/${t(
              Resources.Routes.AccessRoleDetail,
            )}/${role.accessRoleID}`}
          />
        ) : (
          <div></div>
        );

      default:
        return <div>-</div>;
    }
  };

  return (
    <StyledTableWrapper>
      <PrimaryDataTable
        isLoading={isLoading}
        rows={
          accessRoles.accessRoles?.map(role => ({
            data: role,
            rowEntityID: "accessRoleID",
          })) ?? []
        }
        columns={[
          {
            id: "accessRoleCode",
            label: t(Resources.AccessRoles.List.Code),
            width: "30%",
            filter: filter["accessRoleCode"],
            orderBy: orderBy,
          },
          {
            id: "name",
            label: t(Resources.AccessRoles.List.Name),
            cssClass: "name",
            filter: filter["name"],
            orderBy: orderBy,
          },
          {
            id: "detail",
          },
        ]}
        getCell={getCell}
        onRowClick={canEditAccessRoles ? onRowClick : undefined}
        getHeaderCell={column => <div>{column.label}</div>}
        resetTablePage={resetTablePage}
        onPageChange={onPageChange}
        totalCount={accessRoles.count}
        paginationParams={paginationParams}
      />
    </StyledTableWrapper>
  );
};
